import React from 'react'
import FilterFiles from '../../__filter/_files/catalog__filter_files'
import ItemFiles from '../../__item/_files/catalog__item_files'
import Icon from 'blocks.simple/icon/icon'
import { addFont } from 'addstyles'
import { api } from 'core/api/ConnectionManager'
import styles from './catalog__template_files.jcss'
import helpers from 'core/helpers'
import helpersFiles from 'core/helpers/_files'
import translate from 'core/translate'
import { UploadButton } from 'pages/files/__upload/files__upload'
import { userActions } from 'blocks.app/user/user.state'
import filesize from 'filesize'
import { getThemeStyleValue } from 'theme/colors'
import { defaultThemeStyles } from 'blocks.simple/icon/icon.theme'
import { ProgressBar } from 'atoms/ProgressBar'
import { history } from 'features/routes'
import omitEmpty from 'omit-empty'
import store from 'core/store'
import { ItemData } from '../../../../core/models/Template'
import { getURLSearchParamsByLocation } from '../../../../features/routes/utils'
import DotTooltip from '../../../../blocks.simple/dotTooltip/dotTooltip'
import _item from 'blocks.simple/item/item.jcss'
import { CatalogField } from '../../catalog.types'

interface FilesCatalogBreadcrumb {
    name: string
    id?: number
}

interface FilesCatalogContext {
    onFolderOpening: (item: { id: number }, afterCreateAction?: boolean) => void
    query: {
        fileType?: string
    }
    template: {
        fields: () => CatalogField[]
    }
    setState: (
        state: {
            isInit?: boolean
            fields?: CatalogField[]
            breadcrumbs?: FilesCatalogBreadcrumb[]
        },
        callback?: () => void
    ) => void
}

interface FontResponse {
    fileUrl: string
    name: string
}

type CatalogObserverCallback<T> = (data: T) => void

interface CatalogDeleteData {
    id: number
    isDirectory: 0 | 1
}

export default {
    useCustomScroll: true,
    useScrollOnlyForPath: (path: string) => {
        return path.includes('#/files')
    },
    folderName: 'folderId',
    getMethod: 'getFilesAndFolders',
    emptySearchText: () => translate('notFound'),
    emptyFolderText: () => translate('noFiles'),
    emptyIcon: 'not_allow',
    moveItem: (id: number, folderId: number) => api.send('updateFile', { id, folderId }),
    moveFolder: (id: number, folderId: number) => api.send('updateFolder', { id, folderId }),
    multipleMove: (sourceId: number, sourceFolderId: number, parentId: number) => {
        const data = omitEmpty({
            sourceId,
            sourceFolderId,
            parentId,
        })

        return api.send('moveFilesAndFolders', data)
    },
    getDragData: (p_: { item: ItemData }) => {
        return JSON.stringify(helpersFiles.getFileItem(p_.item))
    },
    addItemButton: (self: { query: { type: string }; filter: { folderId?: number | null } }) => {
        if (self.query.type !== 'text' && self.query.type !== 'link' && self.filter && self.filter.folderId) {
            return <UploadButton />
        }
    },
    headerButton: () => <UploadButton mod="fill" title={translate('upload')} withoutFolder={true} />,
    headerFilters: FilterFiles,
    settingsPrefixes: 'files',
    tile: ItemFiles,
    cols: [8, 10],
    wrapperClassName: styles.wrapper,
    cellClassName: styles.cellWrapper,
    mediaCellClassName: styles.mediaCellClassName,
    panelClassName: styles.panelWrapper,
    viewOptions: () => [
        {
            id: 'tile',
            name: translate('tile'),
            icon: 'tile',
            iconColor: getThemeStyleValue('ui', 'icon', 'disable') || defaultThemeStyles.disable,
            iconActiveColor: getThemeStyleValue('ui', 'icon', 'active') || defaultThemeStyles.active,
        },
        {
            id: 'list',
            name: translate('list'),
            icon: 'list',
            iconColor: getThemeStyleValue('ui', 'icon', 'disable') || defaultThemeStyles.disable,
            iconActiveColor: getThemeStyleValue('ui', 'icon', 'active') || defaultThemeStyles.active,
        },
    ],
    sortOptions: () => [
        { id: '-createdAt', name: translate('sortCreatedAt') },
        { id: 'name', name: translate('sortName') },
        { id: '-size', name: translate('sortSize') },
    ],
    getItemModel: (dataItem: ItemData, getSelected: (item: ItemData) => boolean) => {
        const item = { ...dataItem }
        const empty = { fileType: 'empty' }

        if (dataItem.isDirectory) {
            dataItem.content = [empty, empty, empty, empty]
        }

        if (!item.__view) {
            item.__view = {
                selected: getSelected(item),
            }
        }

        return item
    },
    getBackendModel: (item: ItemData) => {
        if (!item.isDirectory) {
            return api.send('getFile', { id: item.id }, { hideLoader: true })
        } else {
            return api.send('getFolder', { folderId: item.id }, { hideLoader: true })
        }
    },
    isEqual: (item: ItemData, prevItem: ItemData) => helpersFiles.isEqual({ item, prevItem }),
    onCreate(this: FilesCatalogContext) {
        const query = getURLSearchParamsByLocation(history.location)

        if (query.selectedFolder) {
            this.onFolderOpening({ id: +query.selectedFolder }, true)
        }
    },
    onCreateListeners: [
        (callback: CatalogObserverCallback<ItemData>, clearList: string[]) =>
            api.addObserver('folderCreated', callback, clearList),
        (callback: CatalogObserverCallback<ItemData>, clearList: string[]) =>
            api.addObserver('fileCreated', callback, clearList),
    ],
    onUpdateListeners: [
        (callback: CatalogObserverCallback<ItemData>, clearList: string[]) =>
            api.addObserver('folderUpdated', callback, clearList),
        (callback: CatalogObserverCallback<ItemData>, clearList: string[]) =>
            api.addObserver('fileUpdated', callback, clearList),
    ],
    onDeleteListeners: [
        (callback: CatalogObserverCallback<CatalogDeleteData>, clearList: string[]) =>
            api.addObserver('folderDeleted', ({ id }: { id: number }) => callback({ id, isDirectory: 1 }), clearList),
        (callback: CatalogObserverCallback<CatalogDeleteData>, clearList: string[]) =>
            api.addObserver('fileDeleted', ({ id }: { id: number }) => callback({ id, isDirectory: 0 }), clearList),
    ],
    isFolder: (item: ItemData) => item.isDirectory,
    tableWrapper: styles.tableWrapper,
    fields: () => [
        { id: 'type', name: translate('type'), hide: true },
        { id: 'fileType', name: translate('type'), hide: true },
        { id: 'name', name: translate('name') },
        { id: 'id', name: 'ID' },
        { id: 'resolution', name: translate('resolution') },
        { id: 'fps', name: 'FPS' },
        { id: 'size', name: translate('size') },
        { id: 'duration', name: translate('durationFull') },
        { id: 'createdAt', name: translate('createdAt') },
        { id: 'updatedAt', name: translate('updatedAt') },
    ],
    init(this: FilesCatalogContext, callback: () => void) {
        this.setState({ isInit: true, fields: this.template.fields() }, callback)

        const state = store.getState()
        const userState = state.user

        if (!userState.isFontsLoaded) {
            api.send<object, FontResponse[]>('getFonts', { withCommon: true }).then((res) => {
                store.dispatch(userActions.setFont())

                res.forEach(function (font: { fileUrl: string; name: string }) {
                    if (font.fileUrl) {
                        addFont(font.fileUrl, `font-family: ${font.name}; font-weight: normal`)
                    }
                })
            })
        }
    },
    getBreadcrumbs(this: FilesCatalogContext, folderId: number) {
        const initBreadcrumb: FilesCatalogBreadcrumb = { name: 'content', id: undefined }

        if (!folderId) {
            this.setState({ breadcrumbs: [initBreadcrumb] })
            return
        }

        api.send<{ folderId: number }, FilesCatalogBreadcrumb[]>('getFolderBreadcrumbs', { folderId }).then(
            (breadcrumbs) => {
                breadcrumbs.reverse()
                breadcrumbs.unshift(initBreadcrumb)
                this.setState({ breadcrumbs })
            }
        )
    },
    moveOnUpdate(
        this: FilesCatalogContext,
        { prev, next }: { prev: { folderId: number }; next: { folderId: number } }
    ) {
        return !this.query.fileType && prev.folderId !== next.folderId
    },
    perPage: 60,
    customElementInSelect(item: { id: number; fileType: string; dataLandscapeStatus: string }, active: boolean) {
        if (item.fileType === 'video' && item.dataLandscapeStatus === 'error') {
            return (
                <Icon
                    className={styles.rotateIconWrapper}
                    type={'rotate'}
                    onClick={function (e) {
                        e.stopPropagation()
                        api.send('retryRotateVideo', { sourceId: item.id })
                    }}
                    size={25}
                    color={active ? 'blue' : 'darkCloud'}
                />
            )
        }
    },
    list: {
        size: (p_: { type: string; progress: number; size: number }) => {
            if (p_.type === 'file') {
                return <div>{p_.progress}%</div>
            }
            if (p_.size >= 0 && p_.size !== null) {
                return <span>{filesize(p_.size, { round: 0 })}</span>
            }
        },
        name: (item: ItemData, active: boolean, _props: unknown, nested: number) => {
            let iconType = 'files'

            if (item.fileType) {
                iconType = helpersFiles.getFileIconName(item)
            }

            if (!item.fileType && item.type) {
                iconType = item.type
            }

            if (item.isDirectory) {
                if (item.hasSmartFolderRulesQ) {
                    iconType = 'smartFiles'
                } else {
                    iconType = 'files'
                }
            }

            return (
                <div
                    className={styles.name}
                    style={{
                        marginLeft: 40 * nested,
                    }}
                >
                    {item.type === 'file' ? (
                        <div>
                            <ProgressBar value={item.progress} strokeWidth={12} className={styles.progressBarStyle} />
                        </div>
                    ) : (
                        <Icon className={styles.icon} type={iconType} />
                    )}

                    <div style={helpersFiles.convertStyles(item.style, { withoutStyles: ['fontSize'] })}>
                        <div style={{ maxWidth: 500 }}>
                            <DotTooltip tooltip={{ html: helpers.strip_html_tags(item.name) }}>
                                <div className={_item.ellipsis}>{helpers.strip_html_tags(item.name)}</div>
                            </DotTooltip>
                        </div>
                    </div>
                </div>
            )
        },
        createdAt: ({ createdAt }: { createdAt: string }) => helpers.getFormattedLocalDate(createdAt, 'DD/MM/YYYY'),
        duration: (listItem: { duration: string }) => {
            if (!listItem.duration) {
                return '-'
            } else {
                const value = (listItem.duration = String(parseInt(listItem.duration, 10)))
                return helpersFiles.convertSeconds(+value)
            }
        },
        updatedAt: ({ updatedAt }: { updatedAt: string }) => helpers.getFormattedLocalDate(updatedAt, 'DD/MM/YYYY'),
        id: (listItem: { id: number }) => {
            if (listItem.id === undefined || listItem.id === null) {
                return '-'
            }

            return listItem.id
        },
        resolution: (listItem: ItemData) => helpersFiles.getFileResolution(listItem) || '-',
        fps: (listItem: ItemData) => {
            const fps = helpersFiles.getFileFps(listItem)
            return fps === null ? '-' : fps
        },
    },
}
